import { createContext, useContext, useState } from "react";
import { Modal } from "react-bootstrap";
import { createPortal } from "react-dom";
import Offcanvas from "react-bootstrap/Offcanvas";
import { wrapDebug } from "./dev_corner";

const ModalContext = createContext({});

export const ModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({ Component: null, props: null });

  const launch = (Component, props) => setModalState({ Component, props });
  const close = () => setModalState({ Component: null, props: null });

  const { Component, props } = modalState;

  return (
    <ModalContext.Provider value={launch}>
      {children}
      {Component && createPortal(<Component {...props} close={close} />, document.body)}
    </ModalContext.Provider>
  );
};

export const useModal = (Component, generalProps = {}) => {
  const launch = useContext(ModalContext);
  return { show: (props) => launch(Component, { ...generalProps, ...props }) };
};

export const ModalBase = ({
  title,
  children,
  close,
  className,
  size,
}: {
  title: string | JSX.Element;
  children: JSX.Element | JSX.Element[];
  close: () => void;
  className?: string;
  size?: "sm" | "lg" | "xl";
}) => (
  <Modal show onHide={close} className={className} size={size}>
    <Modal.Header closeButton>
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
    {wrapDebug(children)}
  </Modal>
);

const w = window as any;

export const Drawer = ({
  title,
  children,
  show,
  onHide,
  trigger,
  placement,
  closeButton,
}: {
  title?: string;
  children: string | JSX.Element | JSX.Element[];
  show?: boolean;
  onHide?: () => void;
  trigger?: string | JSX.Element;
  placement?: "start" | "end" | "bottom" | "top";
  closeButton?: boolean;
}) => {
  const [selfShow, setSelfShow] = useState(false);

  let computedShow;
  let computedHide;

  if (trigger) {
    computedShow = selfShow;
    computedHide = computedHide = () => setSelfShow(false);
  } else {
    computedShow = show;
    computedHide = onHide;
  }

  return (
    <>
      {trigger && <span onClick={() => setSelfShow(true)}>{trigger}</span>}
      <Offcanvas
        show={computedShow}
        onHide={computedHide}
        placement={placement || "end"}
        className={`offcanvas-larger ${placement === "bottom" ? "h-auto" : ""}`}
      >
        {computedShow && (
          <>
            <Offcanvas.Header closeButton className="align-items-start">
              <Offcanvas.Title>{title}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className={placement === "bottom" ? "pb-5" : ""}>
              {children}
              {closeButton && (
                <button
                  type="button"
                  data-bs-dismiss="offcanvas"
                  className="btn btn-link mt-6 no-underline text-center w-100"
                  onClick={computedHide}
                >
                  Close
                </button>
              )}
            </Offcanvas.Body>
          </>
        )}
      </Offcanvas>
    </>
  );
};

export const DrawerBase = ({ title, children, close }) => (
  <Drawer show onHide={close} title={title}>
    {wrapDebug(children)}
  </Drawer>
);
